import { formatSlot, naturalCompare, type NoteIndexEntry } from '@kartothek/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { useCampaign, useNoteIndex, usePrefetchNote, useTypes } from '../hooks';
import { t } from '../i18n';
import { classNames } from '../util';
import { useDiscoveries } from './Discoveries';
import { useNewNote } from './NewNoteDialog';

interface FolderNode {
  name: string;
  path: string;
  folders: FolderNode[];
  notes: NoteIndexEntry[];
}

function buildTree(notes: NoteIndexEntry[]): FolderNode {
  const root: FolderNode = { name: '', path: '', folders: [], notes: [] };
  for (const n of notes) {
    let node = root;
    if (n.folder) {
      let path = '';
      for (const seg of n.folder.split('/')) {
        path = path ? `${path}/${seg}` : seg;
        let child = node.folders.find((f) => f.name === seg);
        if (!child) {
          child = { name: seg, path, folders: [], notes: [] };
          node.folders.push(child);
        }
        node = child;
      }
    }
    node.notes.push(n);
  }
  const sort = (node: FolderNode) => {
    node.folders.sort((a, b) => naturalCompare(a.name, b.name));
    node.notes.sort((a, b) => naturalCompare(a.title, b.title));
    node.folders.forEach(sort);
  };
  sort(root);
  return root;
}

function loadCollapsed(cid: string): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(`kt.collapsed.${cid}`) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { cid = '', nid } = useParams();
  const campaign = useCampaign(cid);
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const newNote = useNewNote();
  const navigate = useNavigate();
  const prefetch = usePrefetchNote(cid);
  const schedule = useQuery({ queryKey: ['schedule', cid], queryFn: () => api.schedule(cid), enabled: !!campaign.data, staleTime: 60_000 });
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsed(cid));
  const gm = !!campaign.data && campaign.data.role !== 'player';
  const discoveries = useDiscoveries(cid, !!campaign.data && !gm);
  const newCount = (discoveries.data ?? []).filter((d) => !d.seen).length;

  const filtered = useMemo(
    () => (index.data ?? []).filter((n) => typeFilter.size === 0 || typeFilter.has(n.typeKey)),
    [index.data, typeFilter],
  );
  const tree = useMemo(() => buildTree(filtered), [filtered]);
  const usedTypes = useMemo(() => {
    const keys = new Set((index.data ?? []).map((n) => n.typeKey));
    return (types.data ?? []).filter((ty) => keys.has(ty.key));
  }, [index.data, types.data]);

  const toggleFolder = (path: string) => {
    const next = new Set(collapsed);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setCollapsed(next);
    try {
      localStorage.setItem(`kt.collapsed.${cid}`, JSON.stringify([...next]));
    } catch {
      /* egal */
    }
  };

  const toggleType = (key: string) => {
    const next = new Set(typeFilter);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setTypeFilter(next);
  };

  const renderFolder = (node: FolderNode, depth: number) => (
    <ul className="tree" key={node.path || '/'}>
      {node.folders.map((f) => {
        const isCollapsed = collapsed.has(f.path) && typeFilter.size === 0;
        return (
          <li key={f.path}>
            <button className="tree-folder" style={{ paddingLeft: 8 + depth * 14 }} onClick={() => toggleFolder(f.path)}>
              <span className="caret">{isCollapsed ? '▸' : '▾'}</span>
              {f.name}
            </button>
            {!isCollapsed && renderFolder(f, depth + 1)}
          </li>
        );
      })}
      {node.notes.map((n) => (
        <li key={n.id}>
          <Link
            to={`/c/${cid}/n/${n.id}`}
            className={classNames('tree-note', n.id === nid && 'active')}
            style={{ paddingLeft: 8 + depth * 14 }}
            onMouseEnter={() => prefetch(n.id)}
            onClick={onNavigate}
          >
            <span className="type-dot" style={{ background: types.byKey.get(n.typeKey)?.color }} />
            <span className="tree-title">{n.title}</span>
            {n.unseen && <span className="new-badge">{t('player.new')}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <Link to="/" className="campaign-switch" title={t('campaigns.switch')}>
          <span className="logo">▤</span>
          <span className="campaign-name">{campaign.data?.name ?? '…'}</span>
        </Link>
      </div>
      <nav className="sidebar-nav">
        {campaign.data && (
          <button
            className="primary"
            onClick={() => newNote.open({ ...(gm ? {} : { typeKey: 'notiz' }), onCreated: (id) => navigate(`/c/${cid}/n/${id}?edit=1`) })}
          >
            + {t('nav.newNote')}
          </button>
        )}
        <button onClick={() => window.dispatchEvent(new Event('kt:quick-switcher'))}>
          {t('nav.quick')} <kbd>Strg K</kbd>
        </button>
        <NavLink to={`/c/${cid}/search`}>{t('nav.search')}</NavLink>
        {!gm && campaign.data && (
          <NavLink to={`/c/${cid}/discoveries`}>
            {t('player.discoveries')} {newCount > 0 && <span className="new-badge">{newCount}</span>}
          </NavLink>
        )}
        <NavLink to={`/c/${cid}/graph`}>{t('nav.graph')}</NavLink>
        {gm && <NavLink to={`/c/${cid}/session`}>{t('session.title')}</NavLink>}
        {gm && <NavLink to={`/c/${cid}/combat`}>{t('combat.title')}</NavLink>}
        <NavLink to={`/c/${cid}/polls`}>
          {t('polls.title')} {(schedule.data?.openPolls ?? 0) > 0 && <span className="new-badge">{schedule.data!.openPolls}</span>}
        </NavLink>
        <NavLink to={`/c/${cid}/quests`}>{t('quests.title')}</NavLink>
        <NavLink to={`/c/${cid}/places`}>{t('hierarchy.places')}</NavLink>
        {gm && <NavLink to={`/c/${cid}/open-links`}>{t('nav.openLinks')}</NavLink>}
        {gm && <NavLink to={`/c/${cid}/import`}>{t('campaigns.import')}</NavLink>}
        {gm && <a href={api.exportUrl(cid)}>{t('campaigns.export')}</a>}
        {gm && <NavLink to={`/c/${cid}/catalog`}>{t('catalog.title')}</NavLink>}
        {!gm && campaign.data?.screenToken && (
          <a href={`/screen/${campaign.data.screenToken}`} target="_blank" rel="noreferrer">
            {t('player.screen')} ↗
          </a>
        )}
        <NavLink to={`/c/${cid}/settings`}>{t('campaigns.settings')}</NavLink>
      </nav>
      {schedule.data?.next && (
        <Link
          className="next-session"
          to={schedule.data.next.sessionNoteId ? `/c/${cid}/n/${schedule.data.next.sessionNoteId}` : `/c/${cid}/polls/${schedule.data.next.pollId}`}
          onClick={onNavigate}
        >
          <span className="small muted">{t('polls.next')}</span>
          <span>{formatSlot(schedule.data.next.startAt, schedule.data.next.endAt, { year: false })}</span>
        </Link>
      )}
      {usedTypes.length > 1 && (
        <div className="type-filter" aria-label={t('nav.filterType')}>
          {usedTypes.map((ty) => (
            <button
              key={ty.key}
              className={classNames('chip', typeFilter.has(ty.key) && 'on')}
              onClick={() => toggleType(ty.key)}
              style={{ ['--chip' as string]: ty.color }}
            >
              {ty.label}
            </button>
          ))}
        </div>
      )}
      <div className="tree-wrap">{index.data ? renderFolder(tree, 0) : <div className="muted pad">{t('common.loading')}</div>}</div>
    </aside>
  );
}
